import { AppShell, Badge, Banner, Button, Card, ChangePasswordPage, EmptyState, ErrorBanner, Loading, NotAllowed, PasswordSessionProvider, PasswordSignInPage, SessionProvider, SignInPage, TextField, hasAnyRole, useAction, useIdempotencyKey, useResource, useSession } from "@gweb/ui";
import { lazy, Suspense, useCallback, useState, type ReactNode } from "react";
import { NavLink, Outlet, Route, Routes, useParams } from "react-router-dom";
import { ApiProvider, PipelineProvider, useApi } from "./context.js";
import { SALES_READ_ROLES } from "./pipeline.js";
import { asApiFailure, type CompetitorDecision, type CompetitorRecord } from "./api.js";
// The route-level code split: the app used to ship as ONE chunk — every screen, every panel and the whole client sat in it, so a reader paid for the audit exporter and the eight dashboards before the pipeline board could be drawn. The router already knows every route, so each screen is a chunk of its own, fetched when its path is opened; React.lazy wants a default export and these modules export named ones, so each factory names the export it wants. One Suspense boundary hangs on the route table (in `Signed`), so the shell, the navigation and the session stay on screen while a screen is fetched, and a route's own parts — the page and the panels hung off it — arrive together.
const AccountDetailPage = lazy(() => import("./pages/AccountDetail.js").then((m) => ({ default: m.AccountDetailPage })));
const AccountsPage = lazy(() => import("./pages/Accounts.js").then((m) => ({ default: m.AccountsPage })));
const AccessReviewPage = lazy(() => import("./pages/AccessReview.js").then((m) => ({ default: m.AccessReviewPage })));
const ApprovalInboxPage = lazy(() => import("./pages/ApprovalInbox.js").then((m) => ({ default: m.ApprovalInboxPage })));
const AuditExportPage = lazy(() => import("./pages/AuditExport.js").then((m) => ({ default: m.AuditExportPage })));
const AuditLogPage = lazy(() => import("./pages/AuditLog.js").then((m) => ({ default: m.AuditLogPage })));
const BoardPage = lazy(() => import("./pages/Board.js").then((m) => ({ default: m.BoardPage })));
const CompensationPage = lazy(() => import("./pages/Compensation.js").then((m) => ({ default: m.CompensationPage })));
const ContractsPage = lazy(() => import("./pages/Contracts.js").then((m) => ({ default: m.ContractsPage })));
const AeDashboardPage = lazy(() => import("./pages/dashboards/Ae.js").then((m) => ({ default: m.AeDashboardPage })));
const ComplianceDashboardPage = lazy(() => import("./pages/dashboards/Compliance.js").then((m) => ({ default: m.ComplianceDashboardPage })));
const DashboardsIndexPage = lazy(() => import("./pages/dashboards/Index.js").then((m) => ({ default: m.DashboardsIndexPage })));
const ExecutiveDashboardPage = lazy(() => import("./pages/dashboards/Executive.js").then((m) => ({ default: m.ExecutiveDashboardPage })));
const OnboardingDashboardPage = lazy(() => import("./pages/dashboards/Onboarding.js").then((m) => ({ default: m.OnboardingDashboardPage })));
const ProductDashboardPage = lazy(() => import("./pages/dashboards/Product.js").then((m) => ({ default: m.ProductDashboardPage })));
const SalesLeadershipDashboardPage = lazy(() => import("./pages/dashboards/SalesLeadership.js").then((m) => ({ default: m.SalesLeadershipDashboardPage })));
const SalesOperationsDashboardPage = lazy(() => import("./pages/dashboards/SalesOperations.js").then((m) => ({ default: m.SalesOperationsDashboardPage })));
const DiscoveryPage = lazy(() => import("./pages/Discovery.js").then((m) => ({ default: m.DiscoveryPage })));
const ForecastPage = lazy(() => import("./pages/Forecast.js").then((m) => ({ default: m.ForecastPage })));
const NewOpportunityPage = lazy(() => import("./pages/NewOpportunity.js").then((m) => ({ default: m.NewOpportunityPage })));
const OpportunityPage = lazy(() => import("./pages/Opportunity.js").then((m) => ({ default: m.OpportunityPage })));
const PricingScenariosPage = lazy(() => import("./pages/PricingScenarios.js").then((m) => ({ default: m.PricingScenariosPage })));
const ProposalsPage = lazy(() => import("./pages/Proposals.js").then((m) => ({ default: m.ProposalsPage })));
const QualificationPage = lazy(() => import("./pages/Qualification.js").then((m) => ({ default: m.QualificationPage })));
const QuotesPage = lazy(() => import("./pages/Quotes.js").then((m) => ({ default: m.QuotesPage })));
const RoutingQueuePage = lazy(() => import("./pages/RoutingQueue.js").then((m) => ({ default: m.RoutingQueuePage })));
const SolutionPage = lazy(() => import("./pages/Solution.js").then((m) => ({ default: m.SolutionPage })));
const TerritoriesPage = lazy(() => import("./pages/Territories.js").then((m) => ({ default: m.TerritoriesPage })));
const ActivationPanel = lazy(() => import("./components/ActivationPanel.js").then((m) => ({ default: m.ActivationPanel })));
const CrossSellPanel = lazy(() => import("./components/CrossSellPanel.js").then((m) => ({ default: m.CrossSellPanel })));
const HierarchyPanel = lazy(() => import("./components/HierarchyPanel.js").then((m) => ({ default: m.HierarchyPanel })));
const LocationRolloutPanel = lazy(() => import("./components/LocationRolloutPanel.js").then((m) => ({ default: m.LocationRolloutPanel })));
const PackagePanel = lazy(() => import("./components/PackagePanel.js").then((m) => ({ default: m.PackagePanel })));

const MINT = "node apps/platform-api/dist/src/mint-token.js --sub <your-name> --roles gweb_sales";
const TITLE = "GWEB Sales";
const TOKEN_KEY = "gweb.session.token";

function Brand() {
  return (
    <>
      <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden="true"><rect width="32" height="32" rx="8" fill="currentColor" opacity="0.14" /><path d="M9 21l5-6 4 4 5-8" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>
      <span>GWEB Sales</span>
    </>
  );
}

/**
 * The account view plus SR-003's hierarchy card: who is above this account, who is below it, and what may not be
 * done with it. The panel reads the account off the path and the hierarchy off the client it is handed, so it is
 * mounted here rather than inside `AccountDetailPage`, which owns the account's own layout.
 *
 * ADR-015's cross-sell feed is mounted here too, as Appendix B's second account-scoped surface: the account is
 * what the recommendations belong to, and the deal they fired on is named on each row rather than being the page.
 */
function AccountDetail() {
  const { id = "" } = useParams();
  const api = useApi();
  return (
    <div className="g-stack">
      <AccountDetailPage />
      <HierarchyPanel accountId={id} api={api} />
      <CrossSellPanel accountId={id} />
    </div>
  );
}

/**
 * The deal's own file: the eight surfaces that hang off one opportunity, in the order the pipeline walks them. The
 * Phase 7 pages are each registered on their own route, which made them reachable only by URL — a route table is not
 * a way in. This is the way in: one navigation bar mounted on every `/opportunities/:id/*` route, so each page can
 * be reached from any other and the reader always knows what else this deal has.
 */
const DEAL_FILE = [
  { to: "", label: "Overview" },
  { to: "discovery", label: "Discovery" },
  { to: "qualification", label: "Qualification" },
  { to: "solution", label: "Solution" },
  { to: "proposals", label: "Proposals" },
  { to: "quotes", label: "Quotes" },
  { to: "pricing-scenarios", label: "Pricing" },
  { to: "contracts", label: "Contracts" },
] as const;

function DealFile() {
  const { id = "" } = useParams();
  if (id === "") return null;
  const base = `/opportunities/${encodeURIComponent(id)}`;
  return (
    <nav className="g-nav" aria-label="This deal">
      {DEAL_FILE.map((entry, i) => (
        <NavLink key={entry.label} to={i === 0 ? base : `${base}/${entry.to}`} end={i === 0}>
          {entry.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** The layout every deal-scoped route renders inside: the deal file above the page, and the page below it. */
function DealLayout() {
  return <div className="g-stack"><DealFile /><Outlet /></div>;
}

/**
 * The opportunity's detail view plus the panels the Phase 7 plan hangs under it: AS-002's multi-product package
 * view, AS-003's per-location rollout board, SR-021's activation record with F-020 / F-021 beneath it, ADR-015's
 * cross-sell feed, and 7.11's competitor record. All five are opportunity-scoped, so they are mounted on the
 * opportunity's own route; the first three read `:id` off the path themselves, and the cross-sell feed is handed the
 * id so it can resolve the account the recommendations belong to.
 */
function OpportunityDetail() {
  const { id = "" } = useParams();
  return (
    <div className="g-stack">
      <OpportunityPage />
      <PackagePanel />
      <LocationRolloutPanel />
      <ActivationPanel />
      <Competitors />
      <CrossSellPanel opportunityId={id} />
    </div>
  );
}

/**
 * Tier **7.11.c** — SR-105's competitor record, as Appendix B's "inline editor" for the Processor change /
 * competitor workstream: the record a `sales-intelligence` sweep surfaced (`POST .../blinkers`) plus the AE's own,
 * and the decision on it.
 *
 * It is mounted here rather than in a component of its own because Appendix E names no file for 7.11 — the plan
 * asks for an editor inside the deal, and `OpportunityPage` is owned by another tier — so it is the same arrangement
 * `PackagePanel` and `LocationRolloutPanel` have: a card on the opportunity's route, reading `:id` from the path.
 *
 * Two things it is careful about:
 *
 * - **A rejection is a decision, not an erase.** A rejected record stays in the listing with the reason that was
 *   given, and `recordCompetitor`'s own answer says so too.
 * - **`approve` is not just a status.** AR-013's re-score trigger rides on the decision, so the answer's `rescore`
 *   is drawn — including `null`, which is what a competitor that is not major returns. Silence would read as "the
 *   re-score fired".
 *
 * The writes are `sales.signal:write` and `sales.signal:approve`; no seller role holds either, so a refusal is the
 * server's own sentence and the listing still reads behind it.
 */
function Competitors() {
  const { id = "" } = useParams();
  const api = useApi();
  const r = useResource(async (signal) => {
    if (id === "") return null;
    return (await api.listCompetitors(id, {}, signal)).data;
  }, [api, id]);

  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [confidence, setConfidence] = useState("");
  const [why, setWhy] = useState("");
  const [decision, setDecision] = useState<CompetitorDecision | null>(null);
  const recordKey = useIdempotencyKey();

  const record = useAction(async () => {
    const out = await api.recordCompetitor(id, {
      name: name.trim(), category: category.trim(),
      // `manual` is the provenance vocabulary's own word for a seller typing one in, and it is the only one this
      // card can honestly claim: the other four doors are `sales-intelligence`, a partner feed and two imports.
      discovered_via: "manual",
      confidence: Number(confidence),
    }, recordKey.key());
    recordKey.renew();
    setName(""); setCategory(""); setConfidence("");
    setDecision(null);
    await r.reload();
    return out;
  });
  const approve = useAction(async (competitor_id: string) => {
    const out = await api.approveCompetitor(competitor_id, recordKey.key());
    recordKey.renew();
    await r.reload();
    setDecision(out);
    return out;
  });
  const reject = useAction(async (competitor_id: string) => {
    const out = await api.rejectCompetitor(competitor_id, why.trim(), recordKey.key());
    recordKey.renew();
    await r.reload();
    setDecision(out);
    return out;
  });
  const refusal = record.error ?? approve.error ?? reject.error;

  const rows = r.data ?? [];
  let body: ReactNode;
  const readFailure = r.error instanceof Error ? (asApiFailure(r.error) ?? null) : null;
  if (r.error && rows.length === 0) {
    // A 404 is a deployment that serves no competitor route at all, or a deal nobody has swept. Either way there is
    // nothing to draw here and the page above is the loud one; every other failure is this card's to report.
    body = readFailure?.status === 404 ? (
      <Banner tone="warn" title="The competitor record could not be read">
        <p>Nothing on this card is this deal&apos;s competitor record. Reload the page to read it again.</p>
      </Banner>
    ) : (
      <ErrorBanner error={r.error} title="Could not load the competitor record" />
    );
  } else if (!r.data) body = <Loading label="Loading the competitor record" />;
  else if (id === "") body = <Banner tone="neutral">The competitor record belongs to one deal, and the page is not showing one.</Banner>;
  else body = (
    <div className="g-stack">
      <p className="g-muted" data-test="competitor-note">
        Who the customer is choosing instead (SR-105 / AS-020). A rejected record stays here: a rejection is a
        decision somebody took, not an erasure.
      </p>
      {rows.length === 0 ? (
        <EmptyState title="No competitor recorded">
          Nothing has been surfaced on this deal. The AE records one below; `sales-intelligence` surfaces candidates
          through its own screening pass.
        </EmptyState>
      ) : (
        <ul className="plain-list" aria-label="Competitors on this deal" data-test="competitor-rows">
          {rows.map((c: CompetitorRecord) => (
            <li key={c.competitor_id} data-test="competitor-row" data-competitor={c.competitor_id} data-status={c.status} data-major={c.major}>
              <div className="g-row">
                <span data-test="competitor-name">{c.name}</span>{" "}
                <Badge tone="neutral">{c.category}</Badge>{" "}
                <span data-test="competitor-status"><Badge tone={c.status === "approved" ? "success" : c.status === "rejected" ? "neutral" : "accent"}>{c.status}</Badge></span>{" "}
                {c.major && <span data-test="competitor-major"><Badge tone="warn">Major</Badge></span>}
              </div>
              <p className="g-muted">
                Confidence <span data-test="competitor-confidence">{c.confidence}</span> · found via {c.discovered_via} · recorded {c.recorded_at}
                {c.major_basis.length > 0 && <> · major on <span data-test="competitor-basis">{c.major_basis.join(", ")}</span></>}
              </p>
              {c.status === "rejected" && (
                <p data-test="competitor-rejection">Rejected by <span data-test="competitor-decided-by">{c.decided_by}</span>: <span data-test="competitor-rejection-reason">{c.rejection_reason}</span></p>
              )}
              <div className="g-row">
                <Button type="button" variant="secondary" disabled={c.status !== "recorded"} onClick={() => { reject.reset(); void approve.run(c.competitor_id); }}>Approve</Button>
                <Button type="button" variant="ghost" disabled={c.status === "approved"} onClick={() => { approve.reset(); void reject.run(c.competitor_id); }}>Reject</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {decision && (
        <div data-test="competitor-decision">
          <Banner tone="success" title={`${decision.competitor.name} is ${decision.competitor.status}`}>
            {decision.rescore
              ? <p data-test="competitor-rescore">AR-013 re-scores on that decision: <span data-test="rescore-rule">{decision.rescore.rule_id}</span>, class <span data-test="rescore-class">{decision.rescore.signal_class}</span>.</p>
              : <p className="g-muted" data-test="competitor-no-rescore">No re-score was triggered: this competitor is not major, and AR-013's trigger rides on the major floor.</p>}
          </Banner>
        </div>
      )}
      <form className="g-stack" aria-label="Record a competitor" onSubmit={(e) => { e.preventDefault(); setDecision(null); record.reset(); void record.run(); }} noValidate>
        <p className="g-label">Record a competitor</p>
        <div className="g-form-grid">
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required hint="As the customer names it. A second record of the same name is refused rather than merged." />
          <TextField label="Category" value={category} onChange={(e) => setCategory(e.target.value)} maxLength={128} required hint="A Product Catalog code: payments, baas, pos, a bundle code, or other." />
          <TextField label="Confidence" inputMode="decimal" value={confidence} onChange={(e) => setConfidence(e.target.value)} maxLength={4} required hint="0.0 to 1.0. At or above 0.7 the record is a major one, which is what AR-013's re-score rides on." />
        </div>
        <TextField label="Rejection reason" value={why} onChange={(e) => setWhy(e.target.value)} maxLength={1000} hint="Kept on the record. Needed to reject; a rejection is not silent." />
        {refusal && <div data-test="competitor-refused"><ErrorBanner error={refusal} title="That was refused" /></div>}
        <div><Button type="submit" variant="secondary" loading={record.running} disabled={name.trim() === "" || category.trim() === "" || confidence.trim() === ""}>Record this competitor</Button></div>
      </form>
    </div>
  );

  return <Card title="Competitors"><div className="g-stack" data-test="competitors">{body}</div></Card>;
}

/**
 * Phase 6's compensation desk, plus the client it reads. The route is registered and so are both reads behind it
 * (`GET /v1/quota/users/:user_id` and its `/attainment` sibling), so this is only the wrapper: the page is asked
 * for no seller and no period of its own, and takes both from the query string.
 */
function Compensation() {
  const api = useApi();
  return <CompensationPage api={api} />;
}

/**
 * The territory grid, tier 6.3.5, on the app's own client.
 *
 * All three routes it reads are registered in `src/api.ts`: `GET /v1/territories` (the definitions and the
 * accounts each one currently matches), `GET /v1/accounts/:account_id/territory` (one account's assignment
 * ledger) and `POST /v1/territories/:territory_id/deactivate`. The page used to be handed a client whose
 * every method threw — the `TERRITORIES_NOT_WIRED` stub — so it drew the refusal; the routes are there now.
 */
function Territories() {
  const api = useApi();
  return <TerritoriesPage api={api} />;
}

/**
 * The forecast desk, tier 6.8.6, on the app's own client.
 *
 * `GET /v1/forecast-period-snapshots/periods` is registered, and it now answers each period with the period's own
 * name as well as its dates — the store derives the label from its own `periodFor`, so the name on screen is the
 * name the store gave it. The client maps the answer into the shape the page reads; the page draws the **snapshot**
 * and never the live `forecast_category` (I-4, SR-078).
 */
function Forecast() {
  const api = useApi();
  return <ForecastPage reader={{ listPeriods: (signal) => api.listForecastPeriods(signal) }} />;
}

/**
 * The approval inbox, tier 6.5.7, on the app's own client.
 *
 * `GET /v1/approvals` is registered, so the inbox reads its listing over the route instead of drawing the
 * refusal it drew while the route did not exist. The read is a prop because the page is read on its own in
 * `ui/test/approval-inbox.test.tsx`; here it is the client's own `listApprovals`, and the page's list is the
 * route's answer.
 *
 * The decide call is **not** wired: `POST /v1/approvals/:id/decide` is registered, but the page's decision needs
 * an idempotency key and a caller that stands at the chain's current step, and that is not this tier's work. The
 * page says so on itself rather than offering a control that cannot be written through.
 */
function Approvals() {
  const api = useApi();
  // A stable read, so the page's resource does not re-read on every render of this component.
  const load = useCallback(async (signal: AbortSignal) => (await api.listApprovals({}, signal)).data, [api]);
  return <ApprovalInboxPage load={load} />;
}

/**
 * The main navigation. Every top-level surface the route table registers is here: the pipeline, the accounts, and
 * the five Sales-operations desks. A registered route that no navigation reaches is a URL somebody has to be told
 * about, which is the same defect as a page that was never mounted — so Phase 6's desks are named here too, next to
 * the two that already were. The Phase 7 pages are deal-scoped and are navigated from the deal file instead
 * (`DealFile`), because none of them means anything without an opportunity to belong to.
 */
const MAIN_NAV = [
  { to: "/", label: "Pipeline", end: true },
  { to: "/accounts", label: "Accounts", end: false },
  { to: "/approvals", label: "Approvals", end: false },
  { to: "/routing-queue", label: "Routing queue", end: false },
  { to: "/forecast", label: "Forecast", end: false },
  { to: "/compensation", label: "Compensation", end: false },
  { to: "/territories", label: "Territories", end: false },
  { to: "/audit", label: "Audit log", end: false },
  { to: "/dashboards", label: "Dashboards", end: false },
] as const;

function Signed() {
  const { claims } = useSession();
  if (!hasAnyRole(claims, SALES_READ_ROLES)) {
    return <AppShell brand={<Brand />}><NotAllowed what="Sales data is for the sales team, Sales Operations, executives and administrators. Sign out, or ask an administrator for one of those roles." /></AppShell>;
  }
  return (
    <AppShell
      brand={<Brand />}
      nav={<>{MAIN_NAV.map((entry) => <NavLink key={entry.to} to={entry.to} end={entry.end}>{entry.label}</NavLink>)}</>}
      footer="GWEB Sales · internal use"
    >
      <PipelineProvider>
        <Suspense fallback={<Loading label="Loading the page" />}><Routes>
          <Route path="/" element={<BoardPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/accounts/:id" element={<AccountDetail />} />
          <Route path="/opportunities/new" element={<NewOpportunityPage />} />
          {/* Everything that hangs off one deal, behind the deal file: the seven Phase 7 pages and the panels
              mounted on the deal's own route. The layout is what makes them reachable from each other. */}
          <Route element={<DealLayout />}>
            <Route path="/opportunities/:id" element={<OpportunityDetail />} />
            <Route path="/opportunities/:id/discovery" element={<DiscoveryPage />} />
            <Route path="/opportunities/:id/qualification" element={<QualificationPage />} />
            <Route path="/opportunities/:id/solution" element={<SolutionPage />} />
            <Route path="/opportunities/:id/proposals" element={<ProposalsPage />} />
            <Route path="/opportunities/:id/quotes" element={<QuotesPage />} />
            <Route path="/opportunities/:id/pricing-scenarios" element={<PricingScenariosPage />} />
            <Route path="/opportunities/:id/contracts" element={<ContractsPage />} />
          </Route>
          {/* Phase 6, Sales operations. One route per page; the four that cannot yet read a full answer say so
              on the page rather than drawing an empty view. */}
          <Route path="/routing-queue" element={<RoutingQueuePage />} />
          <Route path="/territories" element={<Territories />} />
          <Route path="/compensation" element={<Compensation />} />
          <Route path="/approvals" element={<Approvals />} />
          <Route path="/forecast" element={<Forecast />} />
          <Route path="/audit" element={<AuditLogPage />} />
          {/* Tier 1.6, the two Sales-Operations surfaces `src/routes/audit-export.ts` and
              `src/routes/access-review.ts` register. Both pages pick their calls off the shared client
              themselves, so — like `/territories` and `/forecast` — the route is all the app supplies, and a
              client that lost a call makes the page say so rather than drawing an answer it never read.
              Each page carries its own role gate: the export offers no control below the SR-119 ceiling, and
              the review is not shown at all below the SR-114 one. */}
          <Route path="/audit/export" element={<AuditExportPage />} />
          <Route path="/access-review" element={<AccessReviewPage />} />
          {/* Phase 8, tier 8.9.1 — the seven dashboards, one route each.
              The pages were written in tier 8.2.2 and **mounted here for the first time**: before this tier
              `grep -n dashboard ui/src/App.tsx` read nothing, so a board a seller was entitled to see had
              a page and no way to reach it.
              Each page names its own board and nothing else — the body is `DashboardView`, which is the
              only place a board is read, so the seven pages differ in nothing but the id they cite.
              The role check happens before any request: a board the viewer's role may not read is never
              offered, never fetched and never drawn. */}
          {/* The index the nav entry reaches: the boards this reader's roles include, and nothing else. */}
          <Route path="/dashboards" element={<DashboardsIndexPage />} />
          <Route path="/dashboards/executive" element={<ExecutiveDashboardPage />} />
          <Route path="/dashboards/sales-leadership" element={<SalesLeadershipDashboardPage />} />
          <Route path="/dashboards/ae" element={<AeDashboardPage />} />
          <Route path="/dashboards/sales-operations" element={<SalesOperationsDashboardPage />} />
          <Route path="/dashboards/product" element={<ProductDashboardPage />} />
          <Route path="/dashboards/onboarding" element={<OnboardingDashboardPage />} />
          <Route path="/dashboards/compliance" element={<ComplianceDashboardPage />} />
          <Route path="*" element={<NotAllowed what="That page does not exist." />} />
        </Routes></Suspense>
      </PipelineProvider>
    </AppShell>
  );
}

/** The development token, for development only: the page that asks for a pasted token. */
function TokenGate({ apiBase, fetchImpl, onUsePassword }: { apiBase: string; fetchImpl?: typeof fetch; onUsePassword?: () => void }) {
  const { claims } = useSession();
  if (!claims) {
    return (
      <>
        <SignInPage title={TITLE} mintCommand={MINT} />
        {onUsePassword && <p className="g-center"><button type="button" className="g-link" onClick={onUsePassword}>Use your email and password instead</button></p>}
      </>
    );
  }
  return <ApiProvider apiBase={apiBase} {...(fetchImpl && { fetchImpl })}><Signed /></ApiProvider>;
}

/** Email and password, with the session in a cookie the page cannot read. */
function PasswordGate({ apiBase, fetchImpl, onUseToken }: { apiBase: string; fetchImpl?: typeof fetch; onUseToken?: () => void }) {
  const { claims, password } = useSession();
  if (password?.loading) return <div className="g-signin"><Loading label="Checking your sign-in" /></div>;
  if (password?.mustChangePassword) return <ChangePasswordPage title={TITLE} />;
  if (!claims) return <PasswordSignInPage title={TITLE} {...(onUseToken && { onUseToken })} />;
  return <ApiProvider apiBase={apiBase} {...(fetchImpl && { fetchImpl })}><Signed /></ApiProvider>;
}

const storedToken = (): boolean => { try { return sessionStorage.getItem(TOKEN_KEY) !== null; } catch { return false; } };

/**
 * The router is supplied by the caller, so tests can use a memory router and the browser a real one.
 * The app signs in with an email and a password. `allowDevToken` (development builds only) adds a way to paste a
 * development token instead, and keeps working for a tab that already holds one; a production build offers neither.
 */
export function App({ apiBase = "", auth = "password", allowDevToken = false, fetchImpl }: { apiBase?: string; auth?: "password" | "token"; allowDevToken?: boolean; fetchImpl?: typeof fetch }) {
  const [mode, setMode] = useState<"password" | "token">(auth === "token" || (allowDevToken && storedToken()) ? "token" : "password");
  if (mode === "token") {
    return <SessionProvider><TokenGate apiBase={apiBase} {...(fetchImpl && { fetchImpl })} {...(auth === "password" && { onUsePassword: () => setMode("password") })} /></SessionProvider>;
  }
  return <PasswordSessionProvider apiBase={apiBase} {...(fetchImpl && { fetchImpl })}><PasswordGate apiBase={apiBase} {...(fetchImpl && { fetchImpl })} {...(allowDevToken && { onUseToken: () => setMode("token") })} /></PasswordSessionProvider>;
}
